import { EditRecordButton } from "@/components/explorer/RecordEditor";
import { useMemo, useState, useEffect } from "react";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { StatCard } from "@/components/shared/StatCard";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useSearchParams } from "react-router-dom";
import { DeliveryWorkspace } from "@/pages/SuperCrm";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { useCollection } from "@/hooks/useCollection";
import { useDoc } from "@/hooks/useDoc";
import { adapter } from "@/services/db";
import { LivePayments } from "@/components/payments/LivePayments";
import { PayModeSwitch } from "@/components/payments/PayModeSwitch";
import { RazorpayTab } from "@/components/payments/RazorpayTab";
import { WalletsTab } from "@/components/payments/WalletsTab";
import { WalletLedgerTab } from "@/components/payments/WalletLedgerTab";
import { inr, text, fmtTs } from "@/lib/format";

const PAY_TABS = ["rzp", "wallets", "holds", "wpay", "cc", "milestones", "legacy"];

export default function Payments() {
  const [params, setParams] = useSearchParams();
  const payTab = PAY_TABS.includes(params.get("tab")) ? params.get("tab") : "rzp";
  const payments = useCollection("payments");
  const rzp = useCollection("razorpay_payments");
  const wallets = useCollection("customer_payments");
  const users = useCollection("users");
  const [q, setQ] = useState("");

  const userBy = useMemo(() => Object.fromEntries(users.flatMap((u) => [[u.id, u], ...(u.auth_uid ? [[u.auth_uid, u]] : []), ...(u.user_id ? [[u.user_id, u]] : [])])), [users]);
  const paidRzp = useMemo(() => rzp.filter((p) => ["paid", "captured"].includes(p.status)), [rzp]);
  const rzpTotal = paidRzp.reduce((s, p) => s + (Number(p.amount) || 0), 0) / 100;
  const walletTotal = wallets.reduce((s, w) => s + (Number(w.wallet_balance) || 0), 0);
  const rows = payments.filter((p) => !q || JSON.stringify(p).toLowerCase().includes(q.toLowerCase()));

  const { data: sysData } = useDoc("system_status", "global");
  const [fee, setFee] = useState("");
  const [savingFee, setSavingFee] = useState(false);
  useEffect(() => { if (sysData && sysData.start_project_fee != null) setFee(String(sysData.start_project_fee)); }, [sysData]);
  const saveFee = async () => {
    const val = Number(fee);
    if (!val || val < 1) return toast.error("Enter a valid amount in ₹");
    setSavingFee(true);
    try {
      const now = new Date().toISOString();
      await adapter.set("system_status", "global", { start_project_fee: val, updated_at: now });
      await adapter.set("payment_settings", "current", { start_project_fee: val, currency: "INR", updated_at: now });
      toast.success(`"Start a Project" fee set to ₹${val} — live on web now`);
    } catch (e) { toast.error(e?.code === "permission-denied" ? "Publish the system_status admin-write rule first" : e.message); }
    finally { setSavingFee(false); }
  };
  const feeBox = (
    <div className="rounded-xl border border-border bg-card px-3.5 py-2.5" data-testid="start-fee-box">
      <Label className="text-[11px] uppercase tracking-wide text-muted-foreground">“Start a Project” fee</Label>
      <div className="flex items-center gap-2 mt-1">
        <div className="relative">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">₹</span>
          <Input data-testid="start-fee-input" type="number" value={fee} onChange={(e) => setFee(e.target.value)} placeholder="499" className="h-9 w-28 pl-6 font-mono" />
        </div>
        <Button size="sm" className="h-9 gap-1.5" onClick={saveFee} disabled={savingFee} data-testid="start-fee-save"><Icons.Save className="h-4 w-4" />{savingFee ? "Saving…" : "Save"}</Button>
      </div>
      <p className="text-[10px] text-muted-foreground mt-1">Charged on “Start a Project” · currently <span className="font-medium text-foreground">₹{sysData?.start_project_fee ?? "—"}</span></p>
    </div>
  );

  return (
    <div>
      <PageHeader title="Payments & Wallets" subtitle="Every Razorpay payment, customer wallets with credits/debits, checkout holds and wallet-paid orders — live from Firestore." actions={<div className="flex gap-3"><PayModeSwitch />{feeBox}</div>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testId="pay-kpi-count" index={0} label="Razorpay payments" value={String(rzp.length)} icon="CreditCard" accent="primary" />
        <StatCard testId="pay-kpi-total" index={1} label="Collected (paid)" value={inr(rzpTotal)} icon="IndianRupee" accent="emerald" />
        <StatCard testId="pay-kpi-wallets" index={2} label="Wallet balances held" value={inr(walletTotal)} icon="Wallet" accent="cyan" />
        <StatCard testId="pay-kpi-pending" index={3} label="Not paid" value={String(rzp.length - paidRzp.length)} icon="Clock" accent="amber" />
      </div>

      <Tabs value={payTab} onValueChange={(v) => setParams((p) => { p.set("tab", v); return p; }, { replace: true })}>
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="rzp" data-testid="pay-tab-rzp">Razorpay payments</TabsTrigger>
          <TabsTrigger value="wallets" data-testid="pay-tab-wallets">Wallets & transactions</TabsTrigger>
          <TabsTrigger value="holds" data-testid="pay-tab-holds">Wallet holds</TabsTrigger>
          <TabsTrigger value="wpay" data-testid="pay-tab-wpay">Paid from wallet</TabsTrigger>
          <TabsTrigger value="cc" data-testid="pay-tab-cc">CC gateway log</TabsTrigger>
          <TabsTrigger value="milestones" data-testid="pay-tab-milestones">Promise milestone payments</TabsTrigger>
          <TabsTrigger value="legacy" data-testid="pay-tab-legacy">Legacy payments</TabsTrigger>
        </TabsList>
        <TabsContent value="rzp"><RazorpayTab userBy={userBy} /></TabsContent>
        <TabsContent value="wallets"><WalletsTab userBy={userBy} /></TabsContent>
        <TabsContent value="holds"><WalletLedgerTab coll="wallet_holds" userBy={userBy} /></TabsContent>
        <TabsContent value="wpay"><WalletLedgerTab coll="wallet_payments" userBy={userBy} /></TabsContent>
        <TabsContent value="cc"><LivePayments /></TabsContent>
        <TabsContent value="milestones"><DeliveryWorkspace tabs={["payments"]} section="payments" param="mtab" /></TabsContent>
        <TabsContent value="legacy">
          <Card>
            <div className="p-4 border-b border-border">
              <Input data-testid="pay-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search payment, customer, project…" className="max-w-sm" />
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Payment</TableHead><TableHead>Type</TableHead><TableHead>Customer</TableHead><TableHead>Project</TableHead>
                <TableHead>Provider</TableHead><TableHead>Amount</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((p) => (
                  <TableRow key={p.id} data-testid={`payment-row-${p.id}`}>
                    <TableCell className="font-mono text-xs">{p.id}</TableCell>
                    <TableCell className="capitalize text-sm text-muted-foreground">{text(p.kind)}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{text(userBy[p.customer_id]?.name || p.customer_id || p.user_id)}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{text(p.project_id || p.request_id)}</TableCell>
                    <TableCell className="capitalize text-sm text-muted-foreground">{text(p.provider)}</TableCell>
                    <TableCell className="font-mono text-foreground">{inr(p.amount)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtTs(p.created_at)}</TableCell>
                    <TableCell><div className="flex items-center gap-1"><StatusBadge status={p.status || "captured"} /><EditRecordButton path="payments" id={p.id} /></div></TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No payments.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
